import { Tariff, TariffElement } from "../../../../../types/tariff";
import { ContextLabels, getTariffType, isFreeGroup as _isFreeGroup } from "../../../../../utils/tariff";
import {
  CapData,
  FormState,
  HourlyForm,
  HourlyWindow,
  PeriodForm,
  TariffEditType,
  Weekday,
} from "./types";

/** A tariff is "free" when all ENERGY price components have price === 0. */
export const isFreeGroup = (tariff: Tariff): boolean => _isFreeGroup(tariff);

const applyVat = (price: number, vat: number) => price * (1 + vat / 100);
const stripVat = (price: number, vat: number) => price / (1 + vat / 100);

const getEl = (
  tariff: Tariff,
  label: string,
): TariffElement | undefined =>
  tariff.elements.find((e) => e.context_label === label);

const getVat = (tariff: Tariff): number =>
  tariff.elements[0]?.price_components[0]?.vat ?? 19;

export const getPriceWithVat = (tariff: Tariff, label: string): number => {
  const el = getEl(tariff, label);
  if (!el) return 0;
  const { price, vat } = el.price_components[0];
  return applyVat(price, vat);
};

const getMinDurationMin = (tariff: Tariff, label: string): number => {
  const el = getEl(tariff, label);
  return Math.round((el?.restrictions?.min_duration ?? 3600) / 60);
};

/** "HH:mm:ss" → "HH:mm" */
const sliceHHmm = (t: string) => t.slice(0, 5);

/** Returns { start_time, end_time } restrictions for a tariff element, or null */
export const getTimeRestrictions = (tariff: Tariff, label: string) => {
  const r = getEl(tariff, label)?.restrictions;
  return r?.start_time && r?.end_time
    ? { start_time: r.start_time, end_time: r.end_time }
    : null;
};

/** Formats time window restrictions as "HH:mm – HH:mm" */
export const formatTimeWindow = (r: { start_time: string; end_time: string }) =>
  `${sliceHHmm(r.start_time)} – ${sliceHHmm(r.end_time)}`;

/** minutes → "HH:mm" */
export const minToHHmm = (min: number): string => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

/** "HH:mm" → minutes (returns 0 for incomplete mask) */
export const hhmmToMin = (val: string | null | undefined): number => {
  if (!val || val.includes("_")) return 0;
  const [h, m] = val.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

// ─── Hourly parsing ───────────────────────────────────────────────────────────

export const parseHourlyForm = (tariff: Tariff): HourlyForm => {
  const vat = getVat(tariff);

  // Base elements: ENERGY_PRICE / BLOCKING_FEE without start_time restriction
  const baseEnergyEl = tariff.elements.find(
    (e) => e.context_label === "ENERGY_PRICE" && !e.restrictions?.start_time,
  );
  const baseBlockingEl = tariff.elements.find(
    (e) => e.context_label === "BLOCKING_FEE" && !e.restrictions?.start_time,
  );

  const base: PeriodForm = {
    energyFee: baseEnergyEl
      ? parseFloat(applyVat(baseEnergyEl.price_components[0].price, vat).toFixed(2))
      : 0.3,
    blockingFee: baseBlockingEl
      ? parseFloat(applyVat(baseBlockingEl.price_components[0].price, vat).toFixed(2))
      : 0,
    blockingFeeAfterMin: baseBlockingEl
      ? Math.round((baseBlockingEl.restrictions?.min_duration ?? 3600) / 60)
      : 60,
  };

  // Window elements: ENERGY_PRICE entries that have a start_time restriction
  const windowEnergyEls = tariff.elements.filter(
    (e) => e.context_label === "ENERGY_PRICE" && e.restrictions?.start_time,
  );

  const windows: HourlyWindow[] = windowEnergyEls.map((energyEl) => {
    const { start_time, end_time, day_of_week } = energyEl.restrictions!;
    const blockingEl = tariff.elements.find(
      (e) =>
        e.context_label === "BLOCKING_FEE" &&
        e.restrictions?.start_time === start_time &&
        e.restrictions?.end_time === end_time,
    );
    return {
      weekdays: (day_of_week ?? []) as Weekday[],
      startTime: sliceHHmm(start_time!),
      endTime: sliceHHmm(end_time!),
      energyPrice: parseFloat(
        applyVat(energyEl.price_components[0].price, vat).toFixed(2),
      ),
      blockingFeePrice: blockingEl
        ? parseFloat(applyVat(blockingEl.price_components[0].price, vat).toFixed(2))
        : 0,
      blockingFeeAfterMin: blockingEl
        ? Math.round((blockingEl.restrictions?.min_duration ?? 3600) / 60)
        : 60,
    };
  });

  return { base, windows };
};

// ─── Hourly validation ────────────────────────────────────────────────────────

export interface HourlyValidation {
  valid: boolean;
  conflictingDays: Weekday[];
  windowErrors: Array<string | null>;
}

const isValidHHmm = (t: string) => /^\d{2}:\d{2}$/.test(t);

export function validateHourlyForm(windows: HourlyWindow[]): HourlyValidation {
  const windowErrors: Array<string | null> = windows.map((w) => {
    if (w.weekdays.length === 0) return "At least one day must be selected.";
    if (!isValidHHmm(w.startTime) || !isValidHHmm(w.endTime))
      return "Enter valid start and end times.";
    if (w.startTime >= w.endTime) return "Start time must be before end time.";
    return null;
  });

  // Only run overlap check when all individual windows pass basic validation
  const conflictingDays = new Set<Weekday>();
  if (windowErrors.every((e) => e === null)) {
    for (let i = 0; i < windows.length; i++) {
      for (let j = i + 1; j < windows.length; j++) {
        const a = windows[i];
        const b = windows[j];
        const sharedDays = a.weekdays.filter((d) => b.weekdays.includes(d));
        if (sharedDays.length === 0) continue;
        if (a.startTime < b.endTime && b.startTime < a.endTime) {
          sharedDays.forEach((d) => conflictingDays.add(d));
        }
      }
    }
  }

  return {
    valid: windowErrors.every((e) => e === null) && conflictingDays.size === 0,
    conflictingDays: Array.from(conflictingDays),
    windowErrors,
  };
}

// ─── Form initialisation ──────────────────────────────────────────────────────

const DEFAULT_PERIOD: PeriodForm = {
  energyFee: 0.3,
  blockingFee: 0,
  blockingFeeAfterMin: 60,
};

export function initFormFromTariff(tariff: Tariff): FormState {
  const makePeriod = (
    energyLabel: string,
    blockingLabel: string,
  ): PeriodForm => ({
    energyFee: parseFloat(getPriceWithVat(tariff, energyLabel).toFixed(2)),
    blockingFee: parseFloat(getPriceWithVat(tariff, blockingLabel).toFixed(2)),
    blockingFeeAfterMin: getMinDurationMin(tariff, blockingLabel),
  });

  const tariffType = getTariffType(tariff);

  // FREE is stored as a STATIC tariff with all energy prices = 0
  if (!tariffType && isFreeGroup(tariff)) {
    const stat = makePeriod(ContextLabels.enrgyPrice, ContextLabels.blockingFee);
    return {
      type: "FREE",
      static: stat,
      day: stat,
      night: { ...DEFAULT_PERIOD },
      hourly: { base: { ...stat }, windows: [] },
    };
  }

  if (tariffType === "HOURLY") {
    const hourly = parseHourlyForm(tariff);
    return {
      type: "HOURLY",
      static: { ...hourly.base },
      day: { ...hourly.base },
      night: { ...DEFAULT_PERIOD },
      hourly,
    };
  }

  const type: TariffEditType = tariffType === "DAY_NIGHT" ? "DAY_NIGHT" : "STATIC";

  if (type === "DAY_NIGHT") {
    const day = makePeriod(ContextLabels.enrgyPriceDay, ContextLabels.blockingFeeDay);
    const night = makePeriod(ContextLabels.enrgyPriceNight, ContextLabels.blockingFeeNight);
    return {
      type,
      static: day,
      day,
      night,
      hourly: { base: { ...day }, windows: [] },
    };
  }

  const stat = makePeriod(ContextLabels.enrgyPrice, ContextLabels.blockingFee);
  return {
    type,
    static: stat,
    day: stat,
    night: { ...DEFAULT_PERIOD },
    hourly: { base: { ...stat }, windows: [] },
  };
}

// ─── OCPI building ────────────────────────────────────────────────────────────

const DEFAULT_CAP_PRICE_EX_VAT = 20.168067;

const resolveCapPrice = (fee: number, capEl: TariffElement): number => {
  if (fee === 0) return 0;
  return capEl.price_components[0].price > 0
    ? capEl.price_components[0].price
    : DEFAULT_CAP_PRICE_EX_VAT;
};

const makeFallbackCap = (vat: number): TariffElement => ({
  price_components: [{ type: "FLAT", price: DEFAULT_CAP_PRICE_EX_VAT, vat, step_size: 1 }],
  context_label: "BLOCKING_FEE_CAP",
  restrictions: { min_duration: 3600 },
});

export function buildTariff(form: FormState, original: Tariff): Tariff {
  const vat = getVat(original);
  const valWithouVat = (v: number) => stripVat(v, vat);
  const toSec = (min: number) => min * 60;
  const activation = getEl(original, ContextLabels.activation)!;
  const fallbackCap = makeFallbackCap(vat);

  if (form.type === "FREE") {
    const capEl =
      getEl(original, ContextLabels.blockingFeeCap) ??
      getEl(original, ContextLabels.blockingFeeCapDay)!;
    const capMin = capEl?.restrictions?.min_duration;
    return {
      ...original,
      tariff_alt_text: [],
      elements: [
        activation,
        {
          price_components: [{ type: "ENERGY", price: 0, vat, step_size: 1 }],
          context_label: "ENERGY_PRICE",
        },
        {
          price_components: [{ type: "TIME", price: 0, vat, step_size: 1 }],
          context_label: "BLOCKING_FEE",
          restrictions: { min_duration: capMin ?? 3600 },
        },
      ],
    };
  }

  if (form.type === "HOURLY") {
    const capEl =
      getEl(original, ContextLabels.blockingFeeCap) ??
      getEl(original, ContextLabels.blockingFeeCapDay) ??
      fallbackCap;
    const capMin = capEl.restrictions?.min_duration ?? 3600;

    const baseElements: TariffElement[] = [
      activation,
      {
        price_components: [
          { type: "ENERGY", price: valWithouVat(form.hourly.base.energyFee), vat, step_size: 1 },
        ],
        context_label: "ENERGY_PRICE",
      },
      {
        price_components: [
          { type: "TIME", price: valWithouVat(form.hourly.base.blockingFee), vat, step_size: 1 },
        ],
        context_label: "BLOCKING_FEE",
        restrictions: { min_duration: toSec(form.hourly.base.blockingFeeAfterMin) },
      },
      {
        ...capEl,
        context_label: "BLOCKING_FEE_CAP",
        price_components: [
          {
            ...capEl.price_components[0],
            price: resolveCapPrice(form.hourly.base.blockingFee, capEl),
          },
        ],
        restrictions: { min_duration: capMin },
      },
    ];

    const windowElements: TariffElement[] = form.hourly.windows.flatMap((w) => {
      const timeRestrict = {
        start_time: `${w.startTime}:00`,
        end_time: `${w.endTime}:00`,
        day_of_week: w.weekdays,
      };
      return [
        {
          price_components: [
            { type: "ENERGY", price: valWithouVat(w.energyPrice), vat, step_size: 1 },
          ],
          context_label: "ENERGY_PRICE",
          restrictions: timeRestrict,
        },
        {
          price_components: [
            { type: "TIME", price: valWithouVat(w.blockingFeePrice), vat, step_size: 1 },
          ],
          context_label: "BLOCKING_FEE",
          restrictions: { ...timeRestrict, min_duration: toSec(w.blockingFeeAfterMin) },
        },
        {
          ...capEl,
          context_label: "BLOCKING_FEE_CAP",
          price_components: [
            {
              ...capEl.price_components[0],
              price: resolveCapPrice(w.blockingFeePrice, capEl),
            },
          ],
          restrictions: { ...timeRestrict, min_duration: capMin },
        },
      ] as TariffElement[];
    });

    return {
      ...original,
      tariff_alt_text: [{ language: "en", text: "HOURLY" }],
      elements: [...baseElements, ...windowElements],
    };
  }

  if (form.type === "STATIC") {
    const capEl =
      getEl(original, ContextLabels.blockingFeeCap) ??
      getEl(original, ContextLabels.blockingFeeCapDay) ??
      fallbackCap;
    const capMin = capEl.restrictions?.min_duration;
    return {
      ...original,
      tariff_alt_text: [],
      elements: [
        activation,
        {
          price_components: [
            {
              type: "ENERGY",
              price: valWithouVat(form.static.energyFee),
              vat,
              step_size: 1,
            },
          ],
          context_label: "ENERGY_PRICE",
        },
        {
          price_components: [
            {
              type: "TIME",
              price: valWithouVat(form.static.blockingFee),
              vat,
              step_size: 1,
            },
          ],
          context_label: "BLOCKING_FEE",
          restrictions: {
            min_duration: toSec(form.static.blockingFeeAfterMin),
            max_duration: capMin,
          },
        },
        {
          ...capEl,
          context_label: "BLOCKING_FEE_CAP",
          price_components: [
            {
              ...capEl.price_components[0],
              price: resolveCapPrice(form.static.blockingFee, capEl),
            },
          ],
          restrictions: { min_duration: capMin ?? 3600 },
        },
      ],
    };
  }

  const capDayEl =
    getEl(original, ContextLabels.blockingFeeCapDay) ??
    getEl(original, ContextLabels.blockingFeeCap) ??
    fallbackCap;
  const capNightEl =
    getEl(original, ContextLabels.blockingFeeCapNight) ??
    getEl(original, ContextLabels.blockingFeeCap) ??
    fallbackCap;
  const capDay = capDayEl.restrictions?.min_duration;
  const capNight = capNightEl.restrictions?.min_duration;

  const dayTime = getTimeRestrictions(original, ContextLabels.enrgyPriceDay)
    ?? { start_time: "08:00:00", end_time: "20:00:00" };
  const nightTime = getTimeRestrictions(original, ContextLabels.enrgyPriceNight)
    ?? { start_time: "20:00:00", end_time: "08:00:00" };

  return {
    ...original,
    tariff_alt_text: [{ language: "en", text: "DAY_NIGHT" }],
    elements: [
      activation,
      {
        price_components: [
          {
            type: "ENERGY",
            price: valWithouVat(form.day.energyFee),
            vat,
            step_size: 1,
          },
        ],
        context_label: "ENERGY_PRICE_DAY",
        restrictions: dayTime,
      },
      {
        price_components: [
          {
            type: "ENERGY",
            price: valWithouVat(form.night.energyFee),
            vat,
            step_size: 1,
          },
        ],
        context_label: "ENERGY_PRICE_NIGHT",
        restrictions: nightTime ?? undefined,
      },
      {
        price_components: [
          {
            type: "TIME",
            price: valWithouVat(form.day.blockingFee),
            vat,
            step_size: 1,
          },
        ],
        context_label: "BLOCKING_FEE_DAY",
        restrictions: {
          ...(dayTime ?? {}),
          min_duration: toSec(form.day.blockingFeeAfterMin),
          max_duration: capDay,
        },
      },
      {
        price_components: [
          {
            type: "TIME",
            price: valWithouVat(form.night.blockingFee),
            vat,
            step_size: 1,
          },
        ],
        context_label: "BLOCKING_FEE_NIGHT",
        restrictions: {
          ...(nightTime ?? {}),
          min_duration: toSec(form.night.blockingFeeAfterMin),
          max_duration: capNight,
        },
      },
      {
        ...capDayEl,
        context_label: "BLOCKING_FEE_CAP_DAY",
        price_components: [
          {
            ...capDayEl.price_components[0],
            price: resolveCapPrice(form.day.blockingFee, capDayEl),
          },
        ],
        restrictions: { ...(dayTime ?? {}), min_duration: capDay ?? 3600 },
      },
      {
        ...capNightEl,
        context_label: "BLOCKING_FEE_CAP_NIGHT",
        price_components: [
          {
            ...capNightEl.price_components[0],
            price: resolveCapPrice(form.night.blockingFee, capNightEl),
          },
        ],
        restrictions: { ...(nightTime ?? {}), min_duration: capNight ?? 3600 },
      },
    ],
  };
}

export function getCapData(
  tariff: Tariff,
  primary: string,
  fallback: string,
): CapData {
  const el = getEl(tariff, primary) ?? getEl(tariff, fallback);
  if (!el) return { price: 0, afterSec: 0 };
  const { price, vat: v } = el.price_components[0];
  return {
    price: applyVat(price, v),
    afterSec: el.restrictions?.min_duration ?? 0,
  };
}
